import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Phone, KeyRound, Loader2, CheckCircle2, ArrowRight, ArrowLeft, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage({ onLogin }) {
  const { api } = useApp();
  // Step 1: identify, Step 2: choose method, Step 3: OTP verify
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [userName, setUserName] = useState('');
  const [loginMethod, setLoginMethod] = useState('password');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpChannel, setOtpChannel] = useState('sms');
  const [maskedMobile, setMaskedMobile] = useState('');

  // Warm-up pings on mount: fires both a GET (/api/health) AND a POST
  // (/api/auth/warmup) so Cloudflare has fresh edge→origin connections cached
  // for BOTH verbs by the time the user submits credentials. Production 520s
  // are almost always the first POST hitting a stale Cloudflare-origin pool;
  // priming it explicitly here makes that case a no-op.
  const warmUp = useCallback(() => {
    const base = window.location.origin;
    // Bust any Cloudflare edge cache so the GET actually hits origin
    axios.get(`${base}/api/health?t=${Date.now()}`, { timeout: 5000 }).catch(() => {});
    axios.post(`${base}/api/auth/warmup`, {}, { timeout: 5000 }).catch(() => {});
  }, []);
  useEffect(() => { warmUp(); }, [warmUp]);

  const handleIdentify = async (e) => {
    e.preventDefault();
    if (!email || !mobile) { toast.error('Please enter both email and mobile number'); return; }
    setLoading(true);
    setLoadingMsg('Verifying...');
    try {
      const res = await api.loginUser(email, mobile);
      setUserName(res.data.name || '');
      setStep(2);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 404) toast.error('No account found with this email');
      else if (status === 400) toast.error(detail || 'Mobile number does not match');
      else if (!err.response) toast.error('Unable to reach server. Please check your connection and try again.', { duration: 6000 });
      else toast.error(detail || `Server hiccup (${status}). Please try again.`);
    }
    setLoadingMsg('');
    setLoading(false);
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Please enter your password'); return; }
    setLoading(true);
    setLoadingMsg('Signing in...');
    try {
      const res = await api.loginWithPassword(email, password);
      toast.success('Login successful!');
      onLogin(res.data.session_token, res.data.user);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 401) toast.error('Incorrect password');
      else if (detail.includes('not set')) toast.error('Password not set. Use HiTeam login or OTP.');
      else toast.error(detail || `Server hiccup (${status}). Please try again.`);
    }
    setLoadingMsg('');
    setLoading(false);
  };

  const handleHiteamLogin = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Please enter your HiTeam password'); return; }
    setLoading(true);
    setLoadingMsg('Verifying...');
    try {
      const res = await api.loginWithHiteam(email, password);
      toast.success('Login successful!');
      onLogin(res.data.session_token, res.data.user);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 401) toast.error('Invalid HiTeam credentials');
      else toast.error(detail || 'Login failed. Please try again.');
    }
    setLoadingMsg('');
    setLoading(false);
  };

  const handleOtpLimit = (detail) => {
    toast.error((detail && detail.message) || 'Daily OTP limit reached. Please use your panel password.');
    if (detail && detail.has_password) {
      setOtp(''); setLoginMethod('password'); setStep(2);
    } else {
      // No panel password set → send them to the reset/forgot page to create one.
      setTimeout(() => { window.location.href = '/forgot-password'; }, 1400);
    }
  };

  const handleRequestOtp = async () => {
    setOtpChannel('sms');
    setLoading(true);
    setLoadingMsg('Sending OTP...');
    const t1 = setTimeout(() => setLoadingMsg('Connecting...'), 3000);
    const t2 = setTimeout(() => setLoadingMsg('Still connecting...'), 8000);
    try {
      const res = await api.requestLoginOtp(email, mobile);
      setMaskedMobile(res.data.message);
      setStep(3);
      toast.success('OTP sent!');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || '';
      if (status === 403 && detail && detail.code === 'otp_limit') {
        handleOtpLimit(detail);
      } else if (status === 429) {
        toast.error(detail || 'Too many requests. Wait 2-3 minutes.');
      } else if (!err.response || err.code === 'ECONNABORTED') {
        // Timeout — OTP was likely sent, show the input anyway
        setStep(3);
        toast.info('OTP may have been sent. Please check your phone and enter the code below.');
      } else {
        toast.error(detail || 'Failed to send OTP. Try another method.');
      }
    }
    clearTimeout(t1); clearTimeout(t2);
    setLoading(false); setLoadingMsg('');
  };

  const handleRequestEmailOtp = async () => {
    setOtpChannel('email');
    setLoading(true);
    setLoadingMsg('Emailing code...');
    try {
      const res = await api.requestLoginEmailOtp(email);
      setMaskedMobile(res.data.message || `A login code was emailed to ${email}`);
      setStep(3);
      toast.success('Login code emailed!');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 403 && detail && detail.code === 'otp_limit') handleOtpLimit(detail);
      else if (err.response?.status === 429) toast.error(detail || 'Too many code requests. Please wait a few minutes.');
      else toast.error((typeof detail === 'string' && detail) || 'Failed to email code. Please try SMS.');
    }
    setLoading(false); setLoadingMsg('');
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 4) { toast.error('Please enter a valid OTP'); return; }
    setLoading(true);
    try {
      const res = otpChannel === 'email'
        ? await api.verifyLoginEmailOtp(email, otp)
        : await api.verifyLoginOtp(email, otp);
      toast.success('Login successful!');
      onLogin(res.data.session_token, res.data.user);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail && detail.code === 'otp_limit') handleOtpLimit(detail);
      else toast.error((typeof detail === 'string' && detail) || 'Invalid OTP');
    }
    setLoading(false);
  };

  const goBack = () => {
    if (step === 3) { setStep(2); setOtp(''); }
    else if (step === 2) { setStep(1); setPassword(''); setLoginMethod('password'); }
  };

  const methods = [
    { key: 'password', label: 'Panel Password', icon: Lock, desc: 'Use your panel password' },
    { key: 'hiteam', label: 'HiTeam Login', icon: ShieldCheck, desc: 'Use your HiTeam credentials' },
    { key: 'otp', label: 'OTP', icon: Phone, desc: 'Get a one-time password on mobile' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.jpg" alt="CRM" className="w-20 h-20 rounded-2xl mx-auto mb-4 shadow-lg shadow-amber-500/20 object-cover" />
          <h1 className="text-2xl font-bold text-white">CRM</h1>
          <p className="text-slate-400 text-sm mt-1">Manage your team's performance</p>
        </div>

        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardHeader className="text-center pb-2">
            {step > 1 && (
              <button onClick={goBack} className="absolute left-4 top-4 p-1.5 rounded-md hover:bg-slate-100 text-slate-500">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <CardTitle className="text-xl font-bold text-slate-900">
              {step === 1 ? 'Welcome Back' : step === 2 ? `Hi, ${userName || 'User'}` : 'Enter OTP'}
            </CardTitle>
            <CardDescription>
              {step === 1 ? 'Enter your linked email and mobile' : step === 2 ? 'Choose how you want to sign in' : maskedMobile}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">

            {/* Step 1: Email + Mobile */}
            {step === 1 && (
              <form onSubmit={handleIdentify} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="email" data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className="pl-10" required />
                  </div>
                </div>
                <div>
                  <Label htmlFor="mobile" className="text-slate-700">Mobile Number</Label>
                  <div className="relative mt-1.5">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="mobile" data-testid="login-mobile" type="tel" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="9876543210" className="pl-10" required />
                  </div>
                </div>
                <Button type="submit" data-testid="login-continue-btn" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 gap-2">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {loadingMsg || 'Verifying...'}</> : <><ArrowRight className="w-4 h-4" /> Continue</>}
                </Button>
              </form>
            )}

            {/* Step 2: Choose Method & Enter Credentials */}
            {step === 2 && (
              <div className="space-y-4">
                {/* Method Selector */}
                <div className="flex gap-1 p-1 bg-slate-100 rounded-lg" data-testid="login-method-tabs">
                  {methods.map(m => (
                    <button
                      key={m.key}
                      data-testid={`login-tab-${m.key}`}
                      onClick={() => { setLoginMethod(m.key); setPassword(''); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-medium transition-all ${
                        loginMethod === m.key ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <m.icon className="w-3.5 h-3.5" />
                      {m.label}
                    </button>
                  ))}
                </div>

                {/* Password */}
                {loginMethod === 'password' && (
                  <form onSubmit={handlePasswordLogin} className="space-y-4">
                    <div>
                      <Label className="text-slate-700">Panel Password</Label>
                      <div className="relative mt-1.5">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="pl-10" autoFocus required />
                      </div>
                    </div>
                    <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 gap-2">
                      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {loadingMsg || 'Signing in...'}</> : <><ArrowRight className="w-4 h-4" /> Sign In</>}
                    </Button>
                    <div className="text-center">
                      <a
                        data-testid="forgot-password-link"
                        href="/forgot-password"
                        className="text-xs text-amber-700 hover:text-amber-900 hover:underline"
                      >
                        Forgot password? Reset via OTP →
                      </a>
                    </div>
                  </form>
                )}

                {/* HiTeam */}
                {loginMethod === 'hiteam' && (
                  <form onSubmit={handleHiteamLogin} className="space-y-4">
                    <div>
                      <Label className="text-slate-700">HiTeam Password</Label>
                      <div className="relative mt-1.5">
                        <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input data-testid="login-hiteam-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter HiTeam password" className="pl-10" autoFocus required />
                      </div>
                    </div>
                    <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 gap-2">
                      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {loadingMsg || 'Verifying...'}</> : <><ShieldCheck className="w-4 h-4" /> Login with HiTeam</>}
                    </Button>
                  </form>
                )}

                {/* OTP */}
                {loginMethod === 'otp' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500 text-center">
                      We'll send an OTP to your mobile number ending in <span className="font-semibold text-slate-700">...{mobile.slice(-4)}</span>
                    </p>
                    <Button data-testid="request-otp-btn" onClick={handleRequestOtp} disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 gap-2">
                      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {loadingMsg || 'Sending...'}</> : <><Phone className="w-4 h-4" /> Send OTP</>}
                    </Button>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <div className="h-px flex-1 bg-slate-200" /> or <div className="h-px flex-1 bg-slate-200" />
                    </div>
                    <Button
                      data-testid="request-email-otp-btn"
                      onClick={handleRequestEmailOtp}
                      disabled={loading}
                      variant="outline"
                      className="w-full font-medium py-2.5 gap-2 border-slate-300"
                    >
                      <Mail className="w-4 h-4" /> Email me a code instead
                    </Button>
                    <p className="text-[11px] text-slate-400 text-center">SMS not arriving? Get the code on your email.</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: OTP Verify */}
            {step === 3 && (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div>
                  <Label className="text-slate-700">One-Time Password</Label>
                  <div className="relative mt-1.5">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input data-testid="login-otp" type="text" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter OTP" className="pl-10 text-center text-lg tracking-widest font-mono" maxLength={6} autoFocus required />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">{otpChannel === 'email' ? 'Enter the code sent to your email' : 'Enter the OTP sent to your mobile'}</p>
                </div>
                <Button type="submit" data-testid="verify-otp-btn" disabled={loading || otp.length < 4} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 gap-2">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</> : <><CheckCircle2 className="w-4 h-4" /> Verify & Login</>}
                </Button>
                <div className="text-center">
                  <button type="button" onClick={async () => { setLoading(true); try { if (otpChannel === 'email') { await api.requestLoginEmailOtp(email); } else { await api.requestLoginOtp(email, mobile); } toast.success('Code resent!'); } catch { toast.error('Failed to resend'); } setLoading(false); }} disabled={loading} className="text-sm text-amber-600 hover:text-amber-700 font-medium">
                    {otpChannel === 'email' ? 'Resend email code' : 'Resend OTP'}
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-xs mt-6">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
